<template>
    <Head title="Checklist Responses Detailed Report" />

    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
        <!-- Page Header -->
        <div class="flex items-center justify-between">
            <div>
                <Link :href="route('reports.checklists.index')" class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-2 inline-flex items-center">
                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Reports
                </Link>
                <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Checklist Responses Detailed Report</h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    View individual responses with actual answers for each checklist question
                </p>
            </div>
        </div>

        <!-- Filters Section -->
        <Card>
            <div class="p-6">
                <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Filters</h2>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Checklist Name *</label>
                        <Select v-model="filters.template_id" :options="templateOptions" placeholder="Select Checklist Name" @update:modelValue="onTemplateChange" />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date *</label>
                        <input type="date" v-model="filters.start_date" @change="onDateChange" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-gray-100" />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Date *</label>
                        <input type="date" v-model="filters.end_date" @change="onDateChange" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-gray-100" />
                    </div>
                </div>

                <!-- Scope Selection -->
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Report Scope *</label>
                    <div class="grid grid-cols-3 gap-4">
                        <button @click="filters.scope = 'all'" :class="[filters.scope === 'all' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600', 'px-4 py-2 border rounded-md font-medium transition-colors']">
                            All Checklists
                        </button>
                        <button @click="selectScope('location')" :class="[filters.scope === 'location' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600', 'px-4 py-2 border rounded-md font-medium transition-colors']">
                            By Location
                        </button>
                        <button @click="selectScope('asset')" :class="[filters.scope === 'asset' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600', 'px-4 py-2 border rounded-md font-medium transition-colors']">
                            By Asset
                        </button>
                    </div>
                </div>

                <!-- Location Selector -->
                <div v-if="filters.scope === 'location'" class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select Location *</label>
                    <Select v-model="filters.location_id" :options="locationOptions" placeholder="Select Location" :disabled="loadingLocations" />
                    <p v-if="loadingLocations" class="text-sm text-gray-500 mt-1">Loading locations...</p>
                </div>

                <!-- Asset Selector -->
                <div v-if="filters.scope === 'asset'" class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select Asset *</label>
                    <Select v-model="filters.asset_id" :options="assetOptions" placeholder="Select Asset" :disabled="loadingAssets" />
                    <p v-if="loadingAssets" class="text-sm text-gray-500 mt-1">Loading assets...</p>
                </div>

                <!-- Generate and Export Buttons -->
                <div class="flex justify-end items-center gap-2">
                    <Button @click="generateReport" :disabled="!canGenerate || loading" :loading="loading">
                        Generate Report
                    </Button>

                    <Button v-if="reportData && canExport" @click="exportCsv" variant="secondary">
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export CSV
                    </Button>

                    <Button v-if="reportData && canExport" @click="exportPdf" variant="secondary">
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export PDF
                    </Button>
                </div>
            </div>
        </Card>

        <!-- Report Display -->
        <div v-if="reportData" class="space-y-6">
            <!-- Report Header -->
            <Card>
                <div class="p-6">
                    <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">{{ reportData.template_name }}</h2>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <span class="font-medium text-gray-600 dark:text-gray-400">Checklist Code:</span>
                            <span class="ml-2 text-gray-900 dark:text-gray-100">{{ reportData.template_code }}</span>
                        </div>
                        <div>
                            <span class="font-medium text-gray-600 dark:text-gray-400">Date Range:</span>
                            <span class="ml-2 text-gray-900 dark:text-gray-100">{{ reportData.date_range.start }} to {{ reportData.date_range.end }}</span>
                        </div>
                        <div>
                            <span class="font-medium text-gray-600 dark:text-gray-400">Scope:</span>
                            <span class="ml-2 text-gray-900 dark:text-gray-100">{{ reportData.scope }}</span>
                        </div>
                        <div>
                            <span class="font-medium text-gray-600 dark:text-gray-400">Total Checklists:</span>
                            <span class="ml-2 text-gray-900 dark:text-gray-100">{{ reportData.total_checklists }}</span>
                        </div>
                    </div>
                </div>
            </Card>

            <!-- Sections with Fields -->
            <div v-for="(section, sectionIndex) in reportData.sections" :key="sectionIndex" class="space-y-4">
                <!-- Section Header -->
                <Card>
                    <div class="p-6 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500">
                        <h2 class="text-xl font-bold text-blue-900 dark:text-blue-100">{{ section.section_title }}</h2>
                        <p v-if="section.section_description" class="text-sm text-blue-700 dark:text-blue-300 mt-1">{{ section.section_description }}</p>
                    </div>
                </Card>

                <!-- Fields within this section -->
                <div v-for="(field, fieldIndex) in section.fields" :key="fieldIndex" class="ml-6">
                    <Card>
                        <div class="p-6">
                            <!-- Question Header -->
                            <div class="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{{ field.question }}</h3>
                                        <div class="flex items-center gap-2">
                                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                                {{ formatFieldType(field.field_type) }}
                                            </span>
                                            <span v-if="field.is_dependent" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                                Dependent Question
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Dependency Information -->
                                <div v-if="field.is_dependent && field.dependency_info" class="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                                    <div class="text-sm font-medium text-amber-900 dark:text-amber-100 mb-1">Depends on:</div>
                                    <div class="text-sm text-amber-700 dark:text-amber-300 mb-1">{{ field.dependency_info.parent_question }}</div>
                                    <div class="text-xs text-amber-600 dark:text-amber-400 italic">{{ field.dependency_info.rule }}</div>
                                </div>

                                <!-- Desired Answer -->
                                <div v-if="field.desired_answer_display" class="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                                    <div class="text-sm font-medium text-purple-900 dark:text-purple-100 mb-1">Desired Answer:</div>
                                    <div class="text-sm text-purple-700 dark:text-purple-300">{{ field.desired_answer_display }}</div>
                                </div>

                                <!-- Pass/Fail Stats -->
                                <div v-if="field.has_desired_answer && field.pass_fail_stats" class="mt-3 grid grid-cols-2 gap-3">
                                    <div class="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                        <div class="text-xs font-medium text-green-600 dark:text-green-400 mb-1">Passed</div>
                                        <div class="flex items-baseline gap-2">
                                            <span class="text-2xl font-bold text-green-900 dark:text-green-100">{{ field.pass_fail_stats.pass_percentage }}%</span>
                                            <span class="text-sm text-green-600 dark:text-green-400">({{ field.pass_fail_stats.pass_count }} of {{ field.pass_fail_stats.total }})</span>
                                        </div>
                                    </div>
                                    <div class="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                        <div class="text-xs font-medium text-red-600 dark:text-red-400 mb-1">Failed</div>
                                        <div class="flex items-baseline gap-2">
                                            <span class="text-2xl font-bold text-red-900 dark:text-red-100">{{ field.pass_fail_stats.fail_percentage }}%</span>
                                            <span class="text-sm text-red-600 dark:text-red-400">({{ field.pass_fail_stats.fail_count }} of {{ field.pass_fail_stats.total }})</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Individual Responses Table -->
                            <div v-if="field.responses && field.responses.length > 0">
                                <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Individual Responses</h4>
                                <div class="overflow-x-auto">
                                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead class="bg-gray-50 dark:bg-gray-800">
                                            <tr>
                                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completed By</th>
                                                <th v-if="filters.scope === 'all' || filters.scope === 'asset'" class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</th>
                                                <th v-if="filters.scope === 'all' || filters.scope === 'location'" class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Asset</th>
                                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Answer</th>
                                                <th v-if="field.has_desired_answer" class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Result</th>
                                            </tr>
                                        </thead>
                                        <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                            <tr v-for="(response, idx) in field.responses" :key="idx">
                                                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ response.date }}</td>
                                                <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ response.completed_by }}</td>
                                                <td v-if="filters.scope === 'all' || filters.scope === 'asset'" class="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ response.location }}</td>
                                                <td v-if="filters.scope === 'all' || filters.scope === 'location'" class="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">{{ response.asset }}</td>
                                                <td class="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{{ response.answer }}</td>
                                                <td v-if="field.has_desired_answer" class="px-4 py-3 whitespace-nowrap text-sm">
                                                    <span v-if="response.passed" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                                        Passed
                                                    </span>
                                                    <span v-else-if="response.failed" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                                        Failed
                                                    </span>
                                                    <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                                        N/A
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
                                No responses found for this question
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { Head, Link, usePage } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Select from '@/Components/Common/Select.vue';
import route from '@/Utils/route';
import { useToast } from '@/Composables/useToast';
import { useSecureAxios } from '@/Composables/useSecureAxios';

const toast = useToast();
const axios = useSecureAxios();
const page = usePage();

const props = defineProps({
    filterOptions: Object,
    canExport: Boolean,
});

// State
const loading = ref(false);
const loadingLocations = ref(false);
const loadingAssets = ref(false);
const reportData = ref(null);
const locations = ref([]);
const assets = ref([]);
const filters = ref({
    template_id: null,
    start_date: null,
    end_date: null,
    scope: 'all',
    location_id: null,
    asset_id: null,
});

// Computed
const templateOptions = computed(() => {
    return props.filterOptions.templates.map(t => ({
        value: t.id,
        label: `${t.code} - ${t.name}`
    }));
});

const locationOptions = computed(() => {
    return locations.value.map(l => ({
        value: l.id,
        label: l.code ? `${l.code} - ${l.name}` : l.name
    }));
});

const assetOptions = computed(() => {
    return assets.value.map(a => ({
        value: a.id,
        label: a.asset_tag ? `${a.asset_tag} - ${a.name}` : a.name
    }));
});

const canGenerate = computed(() => {
    const basicRequired = filters.value.template_id && filters.value.start_date && filters.value.end_date;
    if (!basicRequired) return false;

    if (filters.value.scope === 'location') {
        return filters.value.location_id !== null;
    } else if (filters.value.scope === 'asset') {
        return filters.value.asset_id !== null;
    }

    return true;
});

// Methods
const onTemplateChange = () => {
    // Reset dependent filters
    filters.value.location_id = null;
    filters.value.asset_id = null;
    locations.value = [];
    assets.value = [];
    reportData.value = null;

    // Load locations/assets if scope is selected and dates are set
    if (filters.value.start_date && filters.value.end_date) {
        if (filters.value.scope === 'location') {
            loadLocations();
        } else if (filters.value.scope === 'asset') {
            loadAssets();
        }
    }
};

const onDateChange = () => {
    // Reset location/asset selections
    filters.value.location_id = null;
    filters.value.asset_id = null;
    locations.value = [];
    assets.value = [];
    reportData.value = null;

    // Reload if needed
    if (filters.value.template_id && filters.value.start_date && filters.value.end_date) {
        if (filters.value.scope === 'location') {
            loadLocations();
        } else if (filters.value.scope === 'asset') {
            loadAssets();
        }
    }
};

const selectScope = (scope) => {
    filters.value.scope = scope;
    filters.value.location_id = null;
    filters.value.asset_id = null;
    reportData.value = null;

    if (filters.value.template_id && filters.value.start_date && filters.value.end_date) {
        if (scope === 'location') {
            loadLocations();
        } else if (scope === 'asset') {
            loadAssets();
        }
    }
};

const loadLocations = async () => {
    if (!filters.value.template_id || !filters.value.start_date || !filters.value.end_date) return;

    loadingLocations.value = true;
    try {
        const response = await axios.post(route('reports.checklists.responses-detailed.locations'), {
            template_id: filters.value.template_id,
            start_date: filters.value.start_date,
            end_date: filters.value.end_date,
        });

        if (response.data.success) {
            locations.value = response.data.locations;
        }
    } catch (error) {
        toast.error('Failed to load locations');
        console.error(error);
    } finally {
        loadingLocations.value = false;
    }
};

const loadAssets = async () => {
    if (!filters.value.template_id || !filters.value.start_date || !filters.value.end_date) return;

    loadingAssets.value = true;
    try {
        const response = await axios.post(route('reports.checklists.responses-detailed.assets'), {
            template_id: filters.value.template_id,
            start_date: filters.value.start_date,
            end_date: filters.value.end_date,
        });

        if (response.data.success) {
            assets.value = response.data.assets;
        }
    } catch (error) {
        toast.error('Failed to load assets');
        console.error(error);
    } finally {
        loadingAssets.value = false;
    }
};

const generateReport = async () => {
    loading.value = true;

    try {
        const response = await axios.post(route('reports.checklists.responses-detailed.generate'), filters.value);

        if (response.data.success) {
            reportData.value = response.data.data;
            toast.success('Report generated successfully');
        }
    } catch (error) {
        toast.error('Failed to generate report');
        console.error(error);
    } finally {
        loading.value = false;
    }
};

const exportPdf = async () => {
    try {
        const response = await axios.post(route('reports.checklists.export-pdf'), {
            report_type: 'responses_detailed',
            data: reportData.value,
            filters: filters.value,
        }, {
            responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `responses-detailed-${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('PDF exported successfully');
    } catch (error) {
        toast.error('Failed to export PDF');
        console.error(error);
    }
};

const exportCsv = async () => {
    try {
        const response = await axios.post(route('reports.checklists.export-csv'), {
            data: reportData.value,
            filters: filters.value,
        }, {
            responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `responses-detailed-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success('CSV exported successfully');
    } catch (error) {
        toast.error('Failed to export CSV');
        console.error(error);
    }
};

const formatFieldType = (type) => {
    const types = {
        'yes_no': 'Yes/No',
        'pass_fail_na': 'Pass/Fail/NA',
        'multiple_choice': 'Multiple Choice',
        'checkboxes': 'Checkboxes',
        'checkbox': 'Checkboxes',
        'multi_select': 'Multiple Selection',
        'multiple_selection': 'Multiple Selection',
        'dropdown': 'Dropdown',
        'dropdown_select': 'Dropdown Select',
        'short_text': 'Short Text',
        'long_text': 'Long Text',
        'text_input': 'Text Input',
        'textarea': 'Text Area',
        'number_input': 'Number',
        'decimal_input': 'Decimal',
        'rating': 'Rating Scale',
        'rating_scale': 'Rating Scale',
        'date': 'Date',
        'date_picker': 'Date Picker',
        'time': 'Time',
        'time_picker': 'Time Picker',
        'datetime': 'Date & Time',
        'datetime_picker': 'Date & Time Picker',
        'photo': 'Photo Upload',
        'photo_capture': 'Photo Capture',
        'file': 'File Upload',
        'file_upload': 'File Upload',
        'signature': 'Signature',
    };
    return types[type] || type;
};
</script>
